import type { ReactNode } from "react";

import type { Currency } from "@krea/shared/types/common";

import { BidTypeEnum } from "@/features/common/enums";
import type {
  IBid,
  IBuildingCreditBid,
  ICompanyLoanBid,
  IFactoringBid,
  ILeasingBid,
  IPurchaseFinancingBid,
  IRealEstateBid,
  IRevolvingCreditBid,
  UnionBidInput,
} from "@/features/lender-responses/types/lender-response";

import { BuildingCreditBid } from "./bid-types/building-credit/BuildingCreditBid";
import { LoanBid } from "./bid-types/company-loan/LoanBid";
import { FactoringBid } from "./bid-types/factoring/FactoringBid";
import { LeasingBid } from "./bid-types/leasing/LeasingBid";
import { PurchaseFinancingBid } from "./bid-types/purchase-financing/PurchaseFinancingBid";
import { RealEstateBid } from "./bid-types/real-estate/RealEstateBid";
import { RevolvingCreditBid } from "./bid-types/revolving-credit/RevolvingCreditBid";

export interface IBidDetailsResponse {
  summary: ReactNode;
  details: ReactNode;
}

export interface IBidDetailsProps {
  bid: IBid | UnionBidInput;
  currency: Currency;
}

const getBidInfo = ({
  bid,
  currency,
}: IBidDetailsProps): IBidDetailsResponse => {
  switch (bid.type) {
    case BidTypeEnum.COMPANY_LOAN:
      return LoanBid({ bid: bid as ICompanyLoanBid, currency });

    case BidTypeEnum.FACTORING:
      return FactoringBid({
        bid: bid as IFactoringBid,
        currency,
      }); // TODO: get numberOfInvoices from applying company
    case BidTypeEnum.REAL_ESTATE:
      return RealEstateBid({ bid: bid as IRealEstateBid, currency });
    case BidTypeEnum.LEASING:
      return LeasingBid({ bid: bid as ILeasingBid, currency });
    case BidTypeEnum.REVOLVING_CREDIT:
      return RevolvingCreditBid({
        bid: bid as IRevolvingCreditBid,
        currency,
      });
    case BidTypeEnum.PURCHASE_FINANCING:
      return PurchaseFinancingBid({
        bid: bid as IPurchaseFinancingBid,
        currency,
      });
    case BidTypeEnum.BUILDING_CREDIT:
      return BuildingCreditBid({
        bid: bid as IBuildingCreditBid,
        currency,
      });
  }
};

// Each bid-type view calls its own hooks, so a bid whose type changes would
// run a different hook sequence in the same component. Keying these by type
// remounts instead.
const BidInfoDetails = (props: IBidDetailsProps) => getBidInfo(props).details;

const BidInfoSummary = (props: IBidDetailsProps) => getBidInfo(props).summary;

export const BidDetails = ({ bid, currency }: IBidDetailsProps) => (
  <div className="rounded-xl border border-solid border-gray-200 bg-white px-3">
    <BidInfoDetails key={bid.type} bid={bid} currency={currency} />
  </div>
);

export const BidSummary = ({ bid, currency }: IBidDetailsProps) => (
  <BidInfoSummary key={bid.type} bid={bid} currency={currency} />
);
